/**
 * FLAGS — `useFeatureEnabled(key, {orgId?, courseId?})`: capa UNIFORME de
 * gating de features en el cliente.
 *
 * Resuelve el estado EFECTIVO (grafo de dependencias incluido) en cascada
 * curso → org → plataforma → habilitada, con las mismas semánticas que el
 * server (`platform/features.rs`):
 *  · plataforma: `GET /api/flags` (todas las filas de `feature_flags`).
 *    Invitado/offline ⇒ sin datos ⇒ default habilitada (local-first honesto).
 *  · org: `org_settings.features` sincronizada (PowerSync, reactiva).
 *  · curso: `course_settings.features` sincronizada.
 *
 * SOLO UX: la autoridad real es el server (`feature_gate` 403 + write-paths
 * de sync). Ocultar UI evita el 403; no es la barrera.
 */
import { type Accessor, createMemo } from "solid-js";
import {
  featureGraph,
  type FeatureOverrides,
  parseFeatureOverrides,
  resolveFeatureEffective,
} from "@space/shared";
import { api, createApiQuery } from "@/lib/api";
import { useActiveOrg } from "@/features/academy/hooks/use-active-org";
import { useOrgSettings } from "@/features/academy/hooks/use-academy-data";
import { useCourseSettings } from "@/features/academy/hooks/use-course-modules";

/** Estado efectivo de una feature + la key culpable si está bloqueada. */
export interface FeatureEnabledState {
  enabled: boolean;
  /** Key que la bloquea (ella misma o un requisito del grafo). */
  blockedBy?: string;
}

interface PublicFlagsResponse {
  success: boolean;
  data: { key: string; enabled: boolean; rollout: number }[];
}

/**
 * Mapa de flags de plataforma (`key → enabled`). Cacheado 60s y tolerante:
 * sin sesión o sin red ⇒ mapa vacío (todas heredan habilitada).
 */
export function usePlatformFlags(): Accessor<Readonly<Record<string, boolean>>> {
  const query = createApiQuery<PublicFlagsResponse, unknown>(
    () => ["flags", "public"],
    async () => api.GET("/api/flags", {}),
    () => ({ staleTime: 60_000, retry: false, refetchOnWindowFocus: false }),
  );
  const flags = createMemo(() => {
    const out: Record<string, boolean> = {};
    for (const f of query.data?.data ?? []) {
      out[f.key] = f.enabled;
    }
    return out;
  });
  return flags;
}

export interface UseFeatureEnabledOptions {
  /** Org a evaluar; por defecto la org ACTIVA del switcher. */
  orgId?: Accessor<string | undefined>;
  /** Curso a evaluar (aplica los overrides de `course_settings`). */
  courseId?: Accessor<string | undefined>;
}

/**
 * Hook uniforme: ¿está `key` efectiva-habilitada en el contexto?
 * Reactivo a switch de org, sync de settings y cambios de flags.
 */
export function useFeatureEnabled(
  key: Accessor<string> | string,
  options: UseFeatureEnabledOptions = {},
): Accessor<FeatureEnabledState> {
  const keyOf = typeof key === "string" ? () => key : key;
  const { activeOrgId } = useActiveOrg();
  // Org explícita si el caller la resuelve; si su accessor devuelve undefined
  // se cae a la org ACTIVA del switcher (contexto por defecto del gating).
  const orgId = () => options.orgId?.() ?? activeOrgId();
  const platform = usePlatformFlags();
  const orgSettings = useOrgSettings(() => orgId());
  const courseSettings = useCourseSettings(() => options.courseId?.());
  const graph = featureGraph();

  const state = createMemo(() => {
    const org: FeatureOverrides = parseFeatureOverrides(orgSettings.data?.features);
    const course: FeatureOverrides = parseFeatureOverrides(courseSettings.data?.features);
    return resolveFeatureEffective(keyOf(), { platformMap: platform(), org, course }, graph);
  });
  return state;
}
